import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
// types
import { ApplyCouponPotentialComms, ApplyCouponPotentialCommsType } from "@swiggy-private/cart-types";
// hooks
import { useFooterCommsVisibility } from "./hooks/useFooterCommsVisibility";
// styles
import { Container, IconContainer, TextContainer, Wrapper, CloseIconWrapper, TipArrow } from "./styles";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { replaceText } from "@swiggy-private/core-ui";
// constants
import { SAVINGS_ITEM_FOOTER_COMMS_DATA_TEST_IDS as IDS } from "@swiggy-private/cart-test-ids";
import { ICON_CLASS } from "./constants";
// components
import { CloseIcon } from "@cart-ui/widgets/close-icon";

export interface FooterCommsStripProps {
    data: ApplyCouponPotentialComms;
}

const FooterCommsStrip = ({ data }: FooterCommsStripProps) => {
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        background_color = "",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        text_color = "",
        text = "",
        icon = "",
        type = ApplyCouponPotentialCommsType.INVALID,
        closeIconColor = "",
        closeIconSize = 28,
        onLoad,
    } = data;

    const [showStrip, setShowStrip] = useState(true);

    const { allowCommsRender, updateFrequencyCounter } = useFooterCommsVisibility(type);

    const isFTUEComms = useMemo(() => {
        return type === ApplyCouponPotentialCommsType.FTUE;
    }, [type]);

    const textVariant = useMemo(() => {
        switch (type) {
            case ApplyCouponPotentialCommsType.FTUE:
                return "V2.body3.neutral.bold";
            default:
                return "V2.overline.neutral.medium";
        }
    }, [type]);

    const iconHTML = useMemo(() => {
        return replaceText(icon, ICON_CLASS, true);
    }, [icon]);

    const shouldRenderStrip = useMemo(() => {
        return allowCommsRender && !isEmpty(text) && !isEmpty(icon) && !isEmpty(iconHTML) && showStrip;
    }, [allowCommsRender, text, icon, iconHTML, showStrip]);

    const handleCloseIconClick = useCallback(() => {
        setShowStrip(updateFrequencyCounter() ?? false);
    }, [updateFrequencyCounter]);

    useEffect(() => {
        if (shouldRenderStrip && typeof onLoad === "function") {
            onLoad();
        }
    }, [shouldRenderStrip, onLoad]);

    if (!shouldRenderStrip) {
        return null;
    }

    return (
        <Wrapper>
            <Container data-testid={IDS.CONTAINER} type={type}>
                <TextContainer
                    data-testid={IDS.TEXT}
                    type={type}
                    bgColor={background_color}
                    textColor={text_color}
                    variant={textVariant}
                >
                    {text}
                </TextContainer>
                <IconContainer data-testid={IDS.ICON} type={type}>
                    {iconHTML}
                </IconContainer>
                {isFTUEComms && <TipArrow data-testid={IDS.TIP_ARROW} />}
                {!isEmpty(closeIconColor) && (
                    <CloseIconWrapper onClick={handleCloseIconClick}>
                        <CloseIcon
                            size={closeIconSize}
                            IconBgColor={background_color}
                            IconFillColor={closeIconColor}
                            dataTestId={IDS.CLOSE_ICON}
                        />
                    </CloseIconWrapper>
                )}
            </Container>
        </Wrapper>
    );
};

export default memo(FooterCommsStrip);
